import { useIntl } from 'react-intl';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  Textarea,
  Checkbox,
  Label,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Button,
} from '@hqo/ui-atoms';
import { useFormMethods } from '@hqo/multi-step-form';
import type { UseFormReturn } from 'react-hook-form';
import {
  HostingType,
  HostStepFormData,
  CreateVisitFormV2Values,
} from 'components/create-visit-form-v2/types/form-data.types';
import { usePaginatedHosts } from 'store/hosts/hooks';
import { useBuildingData } from 'store/building/hooks';
import { getFloorSuiteOptions } from 'components/create-visit-form-v2/utils/get-floor-suite-options';
import { currentUser } from 'store/user/selectors';
import { useSelector } from 'react-redux';
import { useDebounce } from 'hooks/use-debounce.hook';
import { useValidationMessage } from 'components/create-visit-form-v2/hooks/use-validation-message.hook';
import { useVisitStepFieldsConfig } from 'components/create-visit-form-v2/hooks/use-ui-metadata-field-config.hook';

export const HostStep = (): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const fieldsConfig = useVisitStepFieldsConfig();
  const form = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values> | undefined;
  const { data: hostsData, loading, loadingMore, hasMore, loadMore, search } = usePaginatedHosts();
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const user = useSelector(currentUser);
  const commandListRef = useRef<HTMLDivElement>(null);
  const searchValueRef = useRef(searchValue);
  const { floors } = useBuildingData();
  const hasHostPreferencesContent =
    fieldsConfig.hostInvitationCopyEnabled ||
    fieldsConfig.hostCheckinNotificationEnabled ||
    !fieldsConfig.additionalRecipients.hidden;

  // Keep search value ref in sync
  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  // Debounced search function
  const debouncedSearch = useDebounce(() => {
    if (search) {
      search(searchValueRef.current);
    }
  }, 300);

  const hostingType = form?.watch('hostingType');
  const receiveInvitationCopy = form?.watch('receiveInvitationCopy');
  const receiveCheckinNotifications = form?.watch('receiveCheckinNotifications');
  const additionalOrganizers = form?.watch('additionalOrganizers');
  const floor = form?.watch('floor');
  const suite = form?.watch('suite');
  const hostEmail = form?.watch('hostEmail');
  const setValue = form?.setValue;
  const errors = form?.formState?.errors;

  const floorSuiteOptions = useMemo(() => getFloorSuiteOptions(floors), [floors]);

  // Get the current selection value for the select component
  const selectedValue = useMemo(() => {
    if (suite) {
      return `${floor}|${suite}`;
    }
    return floor || '';
  }, [floor, suite]);

  // Create options from hosts data
  const hostOptions = useMemo(() => {
    if (!hostsData) {
      return [];
    }
    return hostsData
      .filter((host) => host.email && host.email.trim() !== '')
      .map((host) => ({
        value: host.email!,
        label: `${host.first_name} ${host.last_name}${host.email ? ` (${host.email})` : ''}`,
      }))
      .filter((option) => option.value && option.value.trim() !== '');
  }, [hostsData]);

  // Handle search input change - triggers API search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearch();
    },
    [debouncedSearch],
  );

  // Handle scroll for infinite loading
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
      if (scrolledToBottom && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loadingMore, loading, loadMore],
  );

  const handleHostSelect = useCallback(
    (email: string) => {
      if (setValue) {
        setValue('hostEmail', email, { shouldValidate: true });
      }
      setTypeaheadOpen(false);
      setSearchValue('');
    },
    [setValue],
  );

  const createSelectHandler = useCallback(
    (email: string) => () => {
      handleHostSelect(email);
    },
    [handleHostSelect],
  );

  const handleClearHost = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (setValue) {
        setValue('hostEmail', '', { shouldValidate: true });
      }
      setTypeaheadOpen(false);
      setSearchValue('');
    },
    [setValue],
  );

  const handleSelectSelf = useCallback(() => {
    if (setValue) {
      setValue('hostingType', HostingType.SELF, { shouldValidate: true });
      setValue('hostEmail', user?.email || '', { shouldValidate: true });
    }
  }, [setValue, user?.email]);

  const handleSelectOther = useCallback(() => {
    if (setValue) {
      setValue('hostingType', HostingType.OTHER, { shouldValidate: true });
      setValue('hostEmail', '', { shouldValidate: false });
    }
  }, [setValue]);

  const handleTextChange = useCallback(
    (fieldName: keyof HostStepFormData, shouldValidate: boolean = false) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (setValue) {
          setValue(fieldName, e.target.value, { shouldValidate });
        }
      },
    [setValue],
  );

  const handleCheckboxChange = useCallback(
    (fieldName: keyof HostStepFormData) => (value: boolean) => {
      if (setValue) {
        setValue(fieldName, value, { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleFloorSuiteChange = useCallback(
    (value: string) => {
      if (!setValue) {
        return;
      }
      // Parse the value to extract floor and suite
      if (value.includes('|')) {
        const [floorValue, suiteValue] = value.split('|');
        setValue('floor', floorValue, { shouldValidate: true });
        setValue('suite', suiteValue, { shouldValidate: true });
      } else {
        // No suite, just floor
        setValue('floor', value, { shouldValidate: true });
        setValue('suite', '', { shouldValidate: true });
      }
    },
    [setValue],
  );

  if (!form || !setValue) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm p-3" data-cy="host-step">
      <div>
        {/* Who is hosting? */}
        {fieldsConfig.alternativeHostsEnabled && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">
              {formatMessage({
                id: 'createVisitFormV2.hostStep.whoIsHosting',
                defaultMessage: 'Who is hosting?',
              })}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={handleSelectSelf}
                className={`h-12 px-6 rounded-lg font-medium transition-colors ${
                  hostingType === 'self'
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border bg-background hover:bg-muted/50'
                }`}
                data-cy="host-type-self-button"
              >
                {formatMessage({
                  id: 'createVisitFormV2.hostStep.iAm',
                  defaultMessage: 'I am',
                })}
              </button>
              <button
                type="button"
                onClick={handleSelectOther}
                className={`h-12 px-6 rounded-lg font-medium transition-colors ${
                  hostingType === 'other'
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border bg-background hover:bg-muted/50'
                }`}
                data-cy="host-type-other-button"
              >
                {formatMessage({
                  id: 'createVisitFormV2.hostStep.someoneElse',
                  defaultMessage: 'Someone else',
                })}
              </button>
            </div>

            {/* Host email field - shown when "Someone else" is selected */}
            {hostingType === 'other' && (
              <div className="mt-4">
                <Label htmlFor="host-email" className="text-sm font-medium mb-2 block">
                  {formatMessage({
                    id: 'createVisitFormV2.hostStep.hostEmail',
                    defaultMessage: 'Host email',
                  })}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Popover open={typeaheadOpen} onOpenChange={setTypeaheadOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={typeaheadOpen}
                      className="w-full justify-between text-left font-normal relative"
                      aria-invalid={!!errors.hostEmail}
                      data-cy="host-email-select-button"
                    >
                      <span className={hostEmail ? 'text-foreground' : 'text-muted-foreground'}>
                        {hostEmail
                          ? hostOptions.find((option) => option.value === hostEmail)?.label || hostEmail
                          : formatMessage({
                              id: 'createVisitFormV2.hostStep.hostEmailPlaceholder',
                              defaultMessage: 'Search or select host email',
                            })}
                      </span>
                      <div className="flex items-center gap-1">
                        {hostEmail && (
                          <button
                            type="button"
                            onClick={handleClearHost}
                            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                            aria-label={formatMessage({
                              id: 'createVisitFormV2.hostStep.clearHost',
                              defaultMessage: 'Clear host',
                            })}
                            data-cy="host-email-clear-button"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        <svg
                          className="ml-2 h-4 w-4 shrink-0 opacity-50"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={formatMessage({
                          id: 'createVisitFormV2.hostStep.searchHost',
                          defaultMessage: 'Search hosts...',
                        })}
                        value={searchValue}
                        onValueChange={handleSearchChange}
                      />
                      <CommandList
                        ref={commandListRef}
                        onScroll={handleScroll}
                        className="max-h-[300px] overflow-y-auto"
                      >
                        {loading ? (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            {formatMessage({
                              id: 'createVisitFormV2.hostStep.loadingHosts',
                              defaultMessage: 'Loading hosts...',
                            })}
                          </div>
                        ) : (
                          <>
                            <CommandEmpty>
                              {formatMessage({
                                id: 'createVisitFormV2.hostStep.noHostsFound',
                                defaultMessage: 'No hosts found.',
                              })}
                            </CommandEmpty>
                            {hostOptions && hostOptions.length > 0 && (
                              <CommandGroup>
                                {hostOptions.map((option) => {
                                  // Ensure value is not empty string
                                  if (!option.value || option.value.trim() === '') {
                                    return null;
                                  }
                                  return (
                                    <CommandItem
                                      key={option.value}
                                      value={option.value}
                                      onSelect={createSelectHandler(option.value)}
                                      className="cursor-pointer"
                                      data-cy="host-email-option"
                                    >
                                      {option.label}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            )}
                            {/* Loading more indicator */}
                            {loadingMore && (
                              <div className="flex items-center justify-center py-3">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                              </div>
                            )}
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.hostEmail && (
                  <p className="text-sm text-destructive mt-1">{formatValidationMessage(errors.hostEmail.message)}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        {fieldsConfig.alternativeHostsEnabled && hasHostPreferencesContent && <div className="border-t mb-6" />}

        {/* Host preferences */}
        {(fieldsConfig.hostInvitationCopyEnabled || fieldsConfig.hostCheckinNotificationEnabled) && (
          <div className="mb-6">
            <h3 className="text-base font-semibold mb-4">
              {formatMessage({
                id: 'createVisitFormV2.hostStep.hostPreferences',
                defaultMessage: 'Host preferences',
              })}
            </h3>
            <div className="space-y-4">
              {fieldsConfig.hostInvitationCopyEnabled && (
                <label
                  htmlFor="receiveInvitationCopy"
                  className="flex items-center gap-3 p-4 rounded-lg border bg-background cursor-pointer hover:bg-muted/20 transition-colors"
                >
                  <Checkbox
                    id="receiveInvitationCopy"
                    checked={receiveInvitationCopy}
                    className="w-5 h-5"
                    onCheckedChange={handleCheckboxChange('receiveInvitationCopy')}
                    data-cy="receive-invitation-copy-checkbox"
                  />
                  <Label htmlFor="receiveInvitationCopy" className="text-sm font-medium cursor-pointer">
                    {formatMessage({
                      id: 'createVisitFormV2.hostStep.receiveCopyOfInvitation',
                      defaultMessage: 'Receive copy of invitation',
                    })}
                  </Label>
                </label>
              )}
              {fieldsConfig.hostCheckinNotificationEnabled && (
                <label
                  htmlFor="receiveCheckinNotifications"
                  className="flex items-center gap-3 p-4 rounded-lg border bg-background cursor-pointer hover:bg-muted/20 transition-colors"
                >
                  <Checkbox
                    id="receiveCheckinNotifications"
                    checked={receiveCheckinNotifications}
                    className="w-5 h-5"
                    onCheckedChange={handleCheckboxChange('receiveCheckinNotifications')}
                    data-cy="receive-checkin-notifications-checkbox"
                  />
                  <Label htmlFor="receiveCheckinNotifications" className="text-sm font-medium cursor-pointer">
                    {formatMessage({
                      id: 'createVisitFormV2.hostStep.receiveCheckInNotifications',
                      defaultMessage: 'Receive check-in notifications',
                    })}
                  </Label>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Additional organizers */}
        {!fieldsConfig.additionalRecipients.hidden && (
          <div className="mb-6">
            <Label htmlFor="additional-organizers" className="text-sm font-medium gap-1">
              {formatMessage({
                id: 'createVisitFormV2.hostStep.additionalOrganizers',
                defaultMessage: 'Additional organizers',
              })}
              {fieldsConfig.additionalRecipients.required && <span className="text-destructive">*</span>}
            </Label>
            <Textarea
              id="additionalOrganizers"
              rows={3}
              placeholder={formatMessage({
                id: 'createVisitFormV2.hostStep.additionalOrganizersPlaceholder',
                defaultMessage: 'Enter email addresses separated by commas',
              })}
              className="w-full mt-2 text-sm resize-none min-h-[84px]"
              value={additionalOrganizers}
              onChange={handleTextChange('additionalOrganizers', true)}
              data-cy="additional-organizers-textarea"
            />
            <div className="min-h-[20px] mt-2">
              {errors.additionalOrganizers ? (
                <p className="text-sm text-destructive">
                  {formatValidationMessage(errors.additionalOrganizers.message)}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {formatMessage({
                    id: 'createVisitFormV2.hostStep.additionalOrganizersHelp',
                    defaultMessage:
                      'Additional organizers will receive host communications (invitations, check-in notifications)',
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        {!fieldsConfig.floor.hidden && floorSuiteOptions.length > 0 && (
          <>
            {/* Divider */}
            <div className="border-t mb-6" />

            {/* Location */}
            <div>
              <h3 className="text-base font-semibold mb-4">
                {formatMessage({
                  id: 'createVisitFormV2.hostStep.location',
                  defaultMessage: 'Location',
                })}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="floor" className="text-sm font-medium gap-1">
                    {formatMessage({
                      id: 'createVisitFormV2.hostStep.floor',
                      defaultMessage: 'Floor',
                    })}
                    {fieldsConfig.floor.required && <span className="text-destructive">*</span>}
                  </Label>
                  <Select value={selectedValue} onValueChange={handleFloorSuiteChange}>
                    <SelectTrigger
                      id="floor"
                      className={`w-full mt-2 ${errors.floor ? 'border-destructive' : ''}`}
                      data-cy="floor-select"
                    >
                      <SelectValue
                        placeholder={formatMessage({
                          id: 'createVisitFormV2.hostStep.selectFloor',
                          defaultMessage: 'Select floor',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {floorSuiteOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.displayLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="min-h-[20px] mt-1">
                    {errors.floor && (
                      <p className="text-sm text-destructive">{formatValidationMessage(errors.floor.message)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
